<template>
    <dashboard-layout>
        <div>
            <h2>Performance in Questions</h2>
            <p v-if="loading">Loading...</p>
            <div v-else>
                <p>Your average score is {{ summary.averageScore }}% after answering {{ summary.totalQuestions }} questions. The average score of registered users is {{ summary.overallAverageScore }}%.</p>
                <p>
                    Performance data is based on first attempts only to try and make the comparison data as meaningful as possible. However, don't be too disheartened if your percentile score is low - remember that some users reset their accounts meaning they may have multiple "first" attempts.
                </p>
                <div class="card rounded shadow border-0 p-2">
                    <div class="d-flex justify-content-between mb-2 border-bottom pt-2 px-2 pb-3">
                        <h6 class="mb-0">Histogram of all users scores</h6>
                    </div>
                    <div class="card-body">
                        <p>Your score of {{ summary.averageScore }}% puts you on the <b>{{ summary.percentileRank }}th percentile</b> of registered users. The histogram below shows the scores of other users.</p>
                        <bar-chart :chart-data="chartData" :options="chartOptions"></bar-chart>
                    </div>
                </div>
            </div>
        </div>
    </dashboard-layout>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import DashboardLayout from '../../dashboard-layout.vue';
import { defineComponent, ref } from 'vue';
import BarChart from "./BarChart.vue";
import { useRoute } from "vue-router";

const toast = useToast();

export default defineComponent({
    components: {
        BarChart,
        DashboardLayout,
    },
    setup() {
        const summary = ref({
            averageScore: 0,
            overallAverageScore: 0,
            percentileRank: 0,
            totalQuestions: 0,
            user_scores: []
        });
        const loading = ref(true);
        const route = useRoute();

        const chartData = ref({
            labels: [],
            datasets: [
                {
                    label: 'User Scores',
                    backgroundColor: [],
                    borderColor: 'rgba(75, 192, 192, 1)',
                    borderWidth: 1,
                    data: []
                }
            ]
        });

        const chartOptions = ref({
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                customLineValue: null // Placeholder for the custom line value
            }
        });

        const getColor = (score) => {
            const green = Math.floor((score / 100) * 255);
            const red = 255 - green;
            return `rgba(${red}, ${green}, 0, 0.7)`;
        };

        const fetchData = () => {
            const id = toast.info('Getting content ...', { icon: 'fa-solid fa-download' });
            axios.get('/performance/summary' , {
                params: {
                    'session_id' : route.params.subscribe_id,
                    'subscribe_id' : route.params.subscribe_id
                }
            })
                .then(response => {
                    summary.value = {
                        averageScore: response.data.average_score.toFixed(1),
                        overallAverageScore: response.data.overall_average_score.toFixed(1),
                        percentileRank: response.data.percentile_rank.toFixed(1),
                        totalQuestions: response.data.total_questions,
                        user_scores: response.data.user_scores.map(score => score.toFixed(1))
                    };

                    const userScores = summary.value.user_scores;
                    chartData.value.labels = Array.from({length: userScores.length}, (_, i) => (i + 1) * (100 / userScores.length));
                    chartData.value.datasets[0].data = userScores;

                    // Assign colors
                    chartData.value.datasets[0].backgroundColor = userScores.map(score => getColor(score));

                    // Set the custom line value for the chart options
                    chartOptions.value.plugins.customLineValue = parseFloat(summary.value.averageScore);

                    loading.value = false;
                    toast.dismiss(id);
                })
                .catch(error => {
                    toast.dismiss(id);
                    toast.error(`Error: ${error.response ? error.response.data.message : 'An error occurred'}`);
                });
        };

        return {
            summary,
            loading,
            chartData,
            chartOptions,
            fetchData
        };
    },
    mounted() {
        this.fetchData();
    }
});
</script>

<style scoped>
#userScoresChart {
    max-width: 600px;
    margin: auto;
}
</style>
